import { atomWithStorage, createJSONStorage } from 'jotai/utils'
import { StorageKeys } from '~/atoms/StorageKeys'

/** How this browser treats Mango Voice links when the desktop app may be installed. */
export enum DesktopAppMode {
  /** Show the "try the desktop app" banner. */
  Ask = 'ask',
  /** Hand external links off to the desktop app. */
  Always = 'always',
  /** Never mention the desktop app. */
  Never = 'never',
}

export interface DesktopAppDetectionBannerState {
  mode?: DesktopAppMode
  /** Legacy "Don't Ask Again" flag; read as {@link DesktopAppMode.Never}. */
  ignoreDesktopApp?: boolean
  lastDismissDate?: string
}

const DESKTOP_APP_MODES = new Set<string>(Object.values(DesktopAppMode))

const isDesktopAppMode = (value: unknown): value is DesktopAppMode =>
  typeof value === 'string' && DESKTOP_APP_MODES.has(value)

/** Resolves the effective mode, migrating the legacy `ignoreDesktopApp` flag. */
export const getDesktopAppMode = (
  state: DesktopAppDetectionBannerState | null | undefined,
): DesktopAppMode => {
  const mode = state?.mode
  if (isDesktopAppMode(mode)) return mode
  return state?.ignoreDesktopApp === true
    ? DesktopAppMode.Never
    : DesktopAppMode.Ask
}

const storage = createJSONStorage<DesktopAppDetectionBannerState>(
  () => localStorage,
)

export const desktopAppDetectionBannerStateAtom =
  atomWithStorage<DesktopAppDetectionBannerState>(
    StorageKeys.DesktopAppDetection,
    {},
    storage,
    { getOnInit: true },
  )

/** Reads the stored state outside React (e.g. before the router mounts). */
export const readDesktopAppDetectionState =
  (): DesktopAppDetectionBannerState => {
    try {
      const raw = localStorage.getItem(StorageKeys.DesktopAppDetection)
      const parsed: unknown = raw ? JSON.parse(raw) : null
      return parsed && typeof parsed === 'object'
        ? (parsed as DesktopAppDetectionBannerState)
        : {}
    } catch {
      return {}
    }
  }
